import type { FormatterPreset } from "@imminently/decisively-core";
import clsx from "clsx";
import React from "react";
import { exampleValue } from "../../../utils/formatterMenus";
import Button from "../../common/Button/Button";
import Icon from "../../common/Icon/Icon";
import TextInput from "../../form/TextInput/TextInput";
import FormatterChainEditor from "../FormatterChain/FormatterChainEditor";
import styles from "./AttributePopover.module.scss";
import type { AttributeOption } from "./types";

/**
 * The one shared panel behind every attribute control (ENG-591).
 *
 * Search + grouped list are always on: they are the point of the component and
 * cannot be switched off. Everything below the list is additive and toggled per
 * consumer via props, and the formatter is always the last row when present:
 *
 *   Base attribute picker : list only
 *   Attribute picker      : + showAffix + showFormatter
 *   Rich-text token       : + showCustomLabel + showFormatter
 *
 * The panel owns its own search `query` and the group quick filter: a row of
 * group pills between the search box and the list, letting the user narrow to
 * one entity group. The user turns it on and off with the toggle in the search
 * box, and that choice is remembered (localStorage) across every attribute
 * popover; it starts off. While a search query is present the pill row narrows
 * to groups whose name matches it (typing "occ" leaves the "Occupancy" pill).
 * Prefix / suffix / custom label / formatter / selection are all controlled by
 * the parent.
 */

export interface AttributePopoverProps {
  attributes: AttributeOption[];
  /** Group headings, in display order. Groups not listed here fall to the end, in first-seen order. */
  groupOrder?: string[];
  /** Key of the currently selected attribute, if any. */
  selectedKey?: string;
  onPick: (attribute: AttributeOption) => void;

  /** A short prompt in a muted footer at the bottom, saying what picking will do (e.g. "Insert attribute at current location…"). */
  hint?: string;

  showAffix?: boolean;
  prefixValue?: string;
  suffixValue?: string;
  onPrefixChange?: (value: string) => void;
  onSuffixChange?: (value: string) => void;

  showCustomLabel?: boolean;
  customLabelValue?: string;
  customLabelPlaceholder?: string;
  onCustomLabelChange?: (value: string) => void;
  onCustomLabelClear?: () => void;

  showFormatter?: boolean;
  /** Menu for the first formatter (the attribute type's set, including None). Later ones also get the text formatters. */
  formatterOptions?: FormatterPreset[];
  /** Formatters applied in order, as in `{{ key | a | b }}`. */
  formatters?: string[];
  onFormattersChange?: (formatters: string[]) => void;

  className?: string;
}

const QUICK_FILTER_STORAGE_KEY = "deci.attributePopover.quickFilter";

/** The user's saved quick-filter choice, or undefined if they haven't made one (or storage is unavailable). */
const readQuickFilter = (): boolean | undefined => {
  try {
    const saved = window.localStorage.getItem(QUICK_FILTER_STORAGE_KEY);
    return saved === null ? undefined : saved === "true";
  } catch {
    return undefined;
  }
};

const writeQuickFilter = (value: boolean) => {
  try {
    window.localStorage.setItem(QUICK_FILTER_STORAGE_KEY, String(value));
  } catch {
    // Storage blocked (private mode, sandbox): the toggle still works for this popover.
  }
};

const NO_FORMATTERS: string[] = [];

interface RenderGroup {
  name: string;
  items: AttributeOption[];
}

/** One row of the list, in display order: what the arrow keys move through. */
type ListRow = { kind: "attribute"; attribute: AttributeOption } | { kind: "all" } | { kind: "group"; group: string };

/** Unique groups present in `attributes`, ordered by `groupOrder` then first-seen. */
function orderGroups(attributes: AttributeOption[], groupOrder: string[]): string[] {
  const seen: string[] = [];
  for (const attribute of attributes) if (!seen.includes(attribute.group)) seen.push(attribute.group);
  return [...groupOrder.filter((g) => seen.includes(g)), ...seen.filter((g) => !groupOrder.includes(g))];
}

function buildGroups(
  attributes: AttributeOption[],
  groupOrder: string[],
  query: string,
  activeGroup: string | null,
): RenderGroup[] {
  const needle = query.trim().toLowerCase();
  const pool = attributes.filter(
    (attribute) =>
      (!activeGroup || attribute.group === activeGroup) && (!needle || attribute.label.toLowerCase().includes(needle)),
  );

  return orderGroups(pool, groupOrder)
    .map((name) => ({ name, items: pool.filter((attribute) => attribute.group === name) }))
    .filter((group) => group.items.length > 0);
}

/** Measured pill widths (px) for the one-line quick filter row. */
export interface PillMetrics {
  /** Width the row has for pills, inside its padding. */
  available: number;
  gap: number;
  all: number;
  /** The "+N" chip, measured as "+99" so it covers two-digit counts. */
  more: number;
  groups: Map<string, number>;
}

/**
 * How many of `order` fit on one line after the "All" pill, leaving room for the
 * "+N" chip when some don't. With nothing measured yet (first paint, or a
 * layout-less test DOM) everything is shown.
 */
export function fitPills(order: string[], metrics: PillMetrics | null): number {
  if (!metrics || metrics.available <= 0) return order.length;
  const { available, gap, all, more, groups } = metrics;
  const widthOf = (count: number) =>
    order.slice(0, count).reduce((total, group) => total + gap + (groups.get(group) ?? 0), all);

  if (widthOf(order.length) <= available) return order.length;
  let count = order.length;
  while (count > 0 && widthOf(count) + gap + more > available) count--;
  return count;
}

/** Attribute count per group. Mirrors the entity totals shown on /build/attributes. */
function groupSizes(attributes: AttributeOption[]): Map<string, number> {
  const sizes = new Map<string, number>();
  for (const attribute of attributes) sizes.set(attribute.group, (sizes.get(attribute.group) ?? 0) + 1);
  return sizes;
}

/**
 * Rank groups for the quick filter: most-populated first (the likeliest place to
 * look), tie-broken by the caller's `groupOrder` then name.
 */
function rankGroups(groups: string[], sizes: Map<string, number>, groupOrder: string[]): string[] {
  const orderIndex = (group: string) => {
    const index = groupOrder.indexOf(group);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...groups].sort(
    (a, b) => (sizes.get(b) ?? 0) - (sizes.get(a) ?? 0) || orderIndex(a) - orderIndex(b) || a.localeCompare(b),
  );
}

/** Stable default, so the memoised group lists (and the pill measuring they drive) don't change every render. */
const NO_GROUP_ORDER: string[] = [];

const sameMetrics = (a: PillMetrics | null, b: PillMetrics) =>
  a !== null &&
  a.available === b.available &&
  a.gap === b.gap &&
  a.all === b.all &&
  a.more === b.more &&
  a.groups.size === b.groups.size &&
  [...b.groups].every(([group, width]) => a.groups.get(group) === width);

export const AttributePopover = ({
  attributes,
  groupOrder = NO_GROUP_ORDER,
  selectedKey,
  onPick,
  hint,
  showAffix = false,
  prefixValue = "",
  suffixValue = "",
  onPrefixChange,
  onSuffixChange,
  showCustomLabel = false,
  customLabelValue = "",
  customLabelPlaceholder = "",
  onCustomLabelChange,
  onCustomLabelClear,
  showFormatter = false,
  formatterOptions,
  formatters = NO_FORMATTERS,
  onFormattersChange,
  className,
}: AttributePopoverProps): React.ReactElement => {
  const [query, setQuery] = React.useState("");
  const [activeGroup, setActiveGroup] = React.useState<string | null>(null);
  const [quickFilter, setQuickFilter] = React.useState(() => readQuickFilter() ?? false);
  /** The "+N" chip swaps the attribute list for the full group list. */
  const [groupListOpen, setGroupListOpen] = React.useState(false);

  const allGroups = React.useMemo(() => orderGroups(attributes, groupOrder), [attributes, groupOrder]);
  const sizes = React.useMemo(() => groupSizes(attributes), [attributes]);
  const rankedGroups = React.useMemo(() => rankGroups(allGroups, sizes, groupOrder), [allGroups, sizes, groupOrder]);

  // Drop the active pill if that group is no longer in the list.
  React.useEffect(() => {
    if (activeGroup && !allGroups.includes(activeGroup)) setActiveGroup(null);
  }, [activeGroup, allGroups]);

  // A new attribute list means the old group list is stale.
  React.useEffect(() => {
    setGroupListOpen(false);
  }, [attributes]);

  const groups = React.useMemo(
    () => buildGroups(attributes, groupOrder, query, activeGroup),
    [attributes, groupOrder, query, activeGroup],
  );
  const showQuickFilter = quickFilter && allGroups.length > 1;

  // A search query narrows the candidate pills to groups whose name contains it.
  const groupQuery = query.trim().toLowerCase();
  const matchedGroups = React.useMemo(
    () => (groupQuery ? rankedGroups.filter((group) => group.toLowerCase().includes(groupQuery)) : rankedGroups),
    [rankedGroups, groupQuery],
  );

  // The pill row is one line. A hidden probe row renders every pill so each one
  // can be measured, and the row shows as many ranked pills as fit, the rest
  // behind a "+N" chip. Re-measured when the row resizes.
  const pillRowRef = React.useRef<HTMLDivElement>(null);
  const pillProbeRef = React.useRef<HTMLDivElement>(null);
  const [pillMetrics, setPillMetrics] = React.useState<PillMetrics | null>(null);

  React.useLayoutEffect(() => {
    const row = pillRowRef.current;
    const probe = pillProbeRef.current;
    if (!showQuickFilter || !row || !probe) return;

    const measure = () => {
      const style = window.getComputedStyle(row);
      const padding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
      const metrics: PillMetrics = {
        available: row.clientWidth - (padding || 0),
        gap: Number.parseFloat(style.columnGap) || 0,
        all: 0,
        more: 0,
        groups: new Map(),
      };
      for (const pill of Array.from(probe.children) as HTMLElement[]) {
        const width = pill.getBoundingClientRect().width;
        if (pill.dataset.probe === "all") metrics.all = width;
        else if (pill.dataset.probe === "more") metrics.more = width;
        else if (pill.dataset.probe) metrics.groups.set(pill.dataset.probe, width);
      }
      setPillMetrics((current) => (sameMetrics(current, metrics) ? current : metrics));
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [showQuickFilter, rankedGroups]);

  // Keep the active group visible even when it wouldn't fit, or the query would
  // otherwise filter it out: it moves to the front of the row.
  const { visiblePills, overflowCount } = React.useMemo(() => {
    const candidates =
      activeGroup && !matchedGroups.includes(activeGroup) ? [activeGroup, ...matchedGroups] : matchedGroups;
    let pills = candidates.slice(0, fitPills(candidates, pillMetrics));
    if (activeGroup && !pills.includes(activeGroup)) {
      const order = [activeGroup, ...candidates.filter((group) => group !== activeGroup)];
      pills = order.slice(0, Math.max(1, fitPills(order, pillMetrics)));
    }
    return { visiblePills: pills, overflowCount: candidates.length - pills.length };
  }, [matchedGroups, activeGroup, pillMetrics]);

  const pickGroup = (group: string | null) => {
    setActiveGroup(group);
    setGroupListOpen(false);
  };

  const toggleQuickFilter = () => {
    const next = !quickFilter;
    setQuickFilter(next);
    writeQuickFilter(next);
    // A group picked while the pills were showing would otherwise keep filtering, invisibly.
    if (!next) pickGroup(null);
  };

  // Keyboard navigation. Focus stays in the search box (an ARIA combobox) while the
  // arrow keys move an active row through whatever list is showing, and Enter picks it.
  const listId = React.useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const rows = React.useMemo<ListRow[]>(
    () =>
      groupListOpen && showQuickFilter
        ? [{ kind: "all" }, ...matchedGroups.map((group): ListRow => ({ kind: "group", group }))]
        : groups.flatMap((group) => group.items.map((attribute): ListRow => ({ kind: "attribute", attribute }))),
    [groupListOpen, showQuickFilter, matchedGroups, groups],
  );
  const [activeRow, setActiveRow] = React.useState(0);
  const rowRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  // A new list starts on the current selection when it's in view, otherwise the first row.
  React.useEffect(() => {
    const selected = rows.findIndex((row) =>
      row.kind === "attribute"
        ? row.attribute.key === selectedKey
        : row.kind === "all"
          ? activeGroup === null
          : row.group === activeGroup,
    );
    setActiveRow(selected === -1 ? 0 : selected);
    // Only when the list itself changes, not when the selection does.
  }, [rows]);

  React.useEffect(() => {
    rowRefs.current[activeRow]?.scrollIntoView?.({ block: "nearest" });
  }, [activeRow]);

  const pickRow = (row: ListRow) => {
    if (row.kind === "attribute") onPick(row.attribute);
    else pickGroup(row.kind === "all" ? null : row.group);
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (rows.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveRow((current) => (current + 1) % rows.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveRow((current) => (current + rows.length - 1) % rows.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = rows[activeRow];
      if (row) pickRow(row);
    }
  };

  /** Props shared by every list row: the option role, its id, and the mouse keeping the active row in step. */
  const rowProps = (index: number) => ({
    id: optionId(index),
    ref: (element: HTMLDivElement | null) => {
      rowRefs.current[index] = element;
    },
    role: "option" as const,
    "aria-selected": index === activeRow,
    className: styles.row,
    "data-active": index === activeRow ? "true" : undefined,
    onMouseMove: () => {
      if (index !== activeRow) setActiveRow(index);
    },
  });

  // Formatter menu options preview on today's date for a date and the attribute's sample otherwise.
  const selectedAttribute = attributes.find((attribute) => attribute.key === selectedKey);

  return (
    <div
      className={clsx(styles.panel, className)}
      data-deci-attribute-popover="true"
    >
      <div className={styles.search}>
        <TextInput
          value={query}
          aria-label="Search attributes"
          role="combobox"
          aria-expanded="true"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={rows.length > 0 ? optionId(activeRow) : undefined}
          onKeyDown={onSearchKeyDown}
          onValueChange={(next) => {
            setQuery(next);
            setGroupListOpen(false);
          }}
          placeholder="Search attributes…"
          startAdornment={
            <Icon
              name="search"
              size={14}
            />
          }
          endAdornment={
            allGroups.length > 1 ? (
              <Button
                icon
                size="sm"
                label={quickFilter ? "Hide group filter" : "Filter by group"}
                selected={quickFilter}
                aria-pressed={quickFilter}
                onClick={toggleQuickFilter}
              >
                <Icon name="settings-2" />
              </Button>
            ) : null
          }
          fullWidth
          autoFocus
        />
      </div>

      {showQuickFilter ? (
        <div
          ref={pillRowRef}
          className={styles.pills}
        >
          {/* Measuring only: every pill, laid out off-screen, so the row knows how many fit. */}
          <div
            ref={pillProbeRef}
            className={styles.pillProbe}
            aria-hidden="true"
          >
            <span
              className={styles.pill}
              data-probe="all"
            >
              All
            </span>
            {rankedGroups.map((group) => (
              <span
                key={group}
                className={styles.pill}
                data-probe={group}
              >
                <span className={styles.pillLabel}>{group}</span>
              </span>
            ))}
            <span
              className={styles.pill}
              data-probe="more"
            >
              +99
            </span>
          </div>
          <button
            type="button"
            className={styles.pill}
            data-active={activeGroup === null}
            onClick={() => pickGroup(null)}
          >
            All
          </button>
          {visiblePills.map((group) => (
            <button
              key={group}
              type="button"
              className={styles.pill}
              data-active={activeGroup === group}
              onClick={() => pickGroup(group)}
              title={group}
            >
              <span className={styles.pillLabel}>{group}</span>
            </button>
          ))}
          {overflowCount > 0 ? (
            <button
              type="button"
              className={styles.pill}
              data-active={groupListOpen}
              onClick={() => setGroupListOpen((open) => !open)}
              title="Show all groups"
            >
              +{overflowCount}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* biome-ignore lint/a11y/useFocusableInteractive: focus stays in the search box, which points here with aria-activedescendant. */}
      <div
        id={listId}
        className={styles.list}
        // biome-ignore lint/a11y/useSemanticElements: a <select> can't be driven from the search box; this is an ARIA combobox list.
        role="listbox"
        aria-label={groupListOpen && showQuickFilter ? "Groups" : "Attributes"}
      >
        {groupListOpen && showQuickFilter ? (
          rows.map((row, index) =>
            row.kind === "attribute" ? null : (
              <div
                key={row.kind === "all" ? "__all" : row.group}
                {...rowProps(index)}
                data-selected={row.kind === "all" ? activeGroup === null : activeGroup === row.group}
                onClick={() => pickRow(row)}
              >
                <span className={styles.rowLabel}>{row.kind === "all" ? "All attributes" : row.group}</span>
                <span className={styles.rowCount}>
                  {row.kind === "all" ? attributes.length : (sizes.get(row.group) ?? 0)}
                </span>
              </div>
            ),
          )
        ) : groups.length === 0 ? (
          <div className={styles.empty}>
            {query.trim() ? `No attributes match “${query}”.` : "No attributes in this group."}
          </div>
        ) : (
          groups.map((group) => (
            <div
              key={group.name}
              // biome-ignore lint/a11y/useSemanticElements: an ARIA option group inside the listbox above.
              role="group"
              aria-label={group.name}
            >
              <div
                className={clsx(styles.microLabel, styles.groupHeading)}
                aria-hidden="true"
              >
                {group.name}
              </div>
              {group.items.map((attribute) => {
                const index = rows.findIndex((row) => row.kind === "attribute" && row.attribute === attribute);
                return (
                  <div
                    key={attribute.key}
                    {...rowProps(index)}
                    data-selected={attribute.key === selectedKey}
                    onClick={() => onPick(attribute)}
                  >
                    {/* Rows always wrap to a full-length line. A cut-off sentence-length label
                        may not be tellable apart from another, so truncation is not allowed here. */}
                    <span className={styles.rowLabel}>{attribute.label}</span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {showAffix ? (
        <div className={clsx(styles.section, styles.affix)}>
          <div className={styles.affixField}>
            <span className={styles.microLabel}>Prefix</span>
            <TextInput
              value={prefixValue}
              aria-label="Prefix"
              onValueChange={(next) => onPrefixChange?.(next)}
              placeholder="none"
              fullWidth
            />
          </div>
          <div className={styles.affixField}>
            <span className={styles.microLabel}>Suffix</span>
            <TextInput
              value={suffixValue}
              aria-label="Suffix"
              onValueChange={(next) => onSuffixChange?.(next)}
              placeholder="none"
              fullWidth
            />
          </div>
        </div>
      ) : null}

      {showCustomLabel ? (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.microLabel}>Display as</span>
            {customLabelValue ? (
              <Button
                label="Reset"
                variant="link"
                size="sm"
                onClick={() => onCustomLabelClear?.()}
              />
            ) : null}
          </div>
          <TextInput
            value={customLabelValue}
            aria-label="Display as"
            onValueChange={(next) => onCustomLabelChange?.(next)}
            placeholder={customLabelPlaceholder}
            fullWidth
          />
        </div>
      ) : null}

      {showFormatter ? (
        <div className={styles.section}>
          <FormatterChainEditor
            options={formatterOptions ?? []}
            formatters={formatters}
            onChange={(next) => onFormattersChange?.(next)}
            exampleValue={exampleValue(selectedAttribute)}
            valueType={selectedAttribute?.type}
          />
        </div>
      ) : null}

      {hint ? <div className={styles.hint}>{hint}</div> : null}
    </div>
  );
};

export default AttributePopover;
